import type {IndexCopilotSpace} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useNavigate} from '@github-ui/react-router'
import {ActionList, ActionMenu, Spinner, Stack} from '@primer/react'
import {useState, useRef, useEffect} from 'react'
import {Icon} from '@github-ui/pacer/Icon'
import {copilotFeatureFlags} from '@github-ui/copilot-chat/utils/copilot-feature-flags'

import {useFetchCopilotSpaces, useCustomCopilotsEnabled} from '../hooks'
import {useRecentCopilotSpaces} from '../hooks/use-recent-copilot-spaces'

import {useSpaceSelection} from '../hooks/use-space-selection'
import {COPILOT_SPACES_NEW_PATH, COPILOT_SPACES_PATH} from '@github-ui/copilot-chat/utils/copilot-chat-helpers'

import {ToggleButton} from '@github-ui/pacer/ToggleButton'
import {CreateSpaceDialog} from './CreateSpaceDialog'

export function SpacesSelect() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()
  const {handleSpaceSelect: setPendingSpace, clearSpaceSelection, activeCopilotSpaceId, readOnly} = useSpaceSelection()
  const loadingStatusRef = useRef<HTMLDivElement>(null)
  const firstActionItemRef = useRef<HTMLLIElement>(null)

  const [showCreateSpaceDialog, setShowCreateSpaceDialog] = useState(false)

  const closeCreateSpaceDialog = () => {
    sendEvent('dotcom_chat.activate', {target: 'COPILOT_SPACE_CREATE_DIALOG_CLOSE', mode: 'immersive'})
    setShowCreateSpaceDialog(false)
  }

  const handleCreateSpace = () => {
    if (copilotFeatureFlags.spacesV3UI) {
      setShowCreateSpaceDialog(true)
    } else {
      navigate(COPILOT_SPACES_NEW_PATH)
    }
    sendEvent('dotcom_chat.activate', {
      target: 'SPACES_INPUT_CREATE_SPACE',
      mode: 'immersive',
    })
  }

  const handleViewAllSpaces = () => {
    navigate(COPILOT_SPACES_PATH)
    sendEvent('dotcom_chat.activate', {
      target: 'SPACES_INPUT_VIEW_ALL_SPACES',
      mode: 'immersive',
    })
  }

  const handleSpaceSelect = (space: IndexCopilotSpace) => {
    const isCurrentlySelected =
      activeCopilotSpaceId && space.id === activeCopilotSpaceId.id && space.owner === activeCopilotSpaceId.owner

    if (isCurrentlySelected) {
      clearSpaceSelection()
    } else {
      setPendingSpace({id: space.id, owner: space.owner})
      sendEvent('dotcom_chat.activate', {
        target: 'SPACES_INPUT_SELECT_SPACE',
        space: `${space.owner}/${space.id}`,
      })
    }
    setOpen(false)
  }

  const {data: sortedSpaces = [], isLoading: isLoadingRecentSpaces} = useRecentCopilotSpaces()
  const {data: allSpaces = [], isLoading: isLoadingAllSpaces} = useFetchCopilotSpaces(true)

  const copilotSpacesEnabled = useCustomCopilotsEnabled()

  const isLoading = isLoadingRecentSpaces || isLoadingAllSpaces

  // Focus the loading status when it becomes visible, and focus first action item when loading completes
  useEffect(() => {
    if (isLoading && open && loadingStatusRef.current) {
      loadingStatusRef.current.focus()
    } else if (!isLoading && open && firstActionItemRef.current) {
      // Small delay to ensure DOM has updated
      const timeoutId = setTimeout(() => {
        firstActionItemRef.current?.focus()
      }, 0)
      return () => clearTimeout(timeoutId)
    }
  }, [isLoading, open])

  // If readOnly (if a thread exists) hide the menu. We can't yet assign
  // a space to an existing thread.
  if (!copilotSpacesEnabled || readOnly) {
    return
  }

  // Find the selected space in allSpaces
  const selectedSpaceData = activeCopilotSpaceId
    ? allSpaces.find(space => space.id === activeCopilotSpaceId.id && space.owner === activeCopilotSpaceId.owner)
    : undefined

  // Use the space's name and blue color if found, otherwise use default
  const displayName = selectedSpaceData ? selectedSpaceData.name : 'Space'

  const menuWidth = '260px'
  return (
    <>
      <ActionMenu open={open} onOpenChange={setOpen}>
        <ActionMenu.Anchor>
          <ToggleButton
            icon={<Icon icon="spaces" />}
            isSelected={!!selectedSpaceData}
            onClick={() => {}} // ActionMenu will handle toggling
            hasMenu
            aria-label="Select a copilot space for this conversation"
            isMenuOpen={open}
            selectedItemLabel={displayName}
          >
            {displayName}
          </ToggleButton>
        </ActionMenu.Anchor>
        <ActionMenu.Overlay style={sortedSpaces.length > 0 ? {width: menuWidth} : {}}>
          {isLoading ? (
            <Stack direction="horizontal" align="center" justify="center" padding="normal" style={{width: menuWidth}}>
              <div ref={loadingStatusRef} role="status" className="sr-only" tabIndex={-1}>
                Loading spaces
              </div>
              <Spinner />
            </Stack>
          ) : (
            <ActionList selectionVariant="single" role="menu" aria-label="Select a space">
              {sortedSpaces.length > 0 ? (
                <>
                  <ActionList.Group>
                    <ActionList.GroupHeading aria-label="Recent spaces">Recent</ActionList.GroupHeading>
                    {sortedSpaces.map((space, index) => {
                      const isSelected =
                        !!activeCopilotSpaceId &&
                        space.id === activeCopilotSpaceId.id &&
                        space.owner === activeCopilotSpaceId.owner
                      return (
                        <ActionList.Item
                          ref={index === 0 ? firstActionItemRef : undefined}
                          role="menuitemradio"
                          key={`${space.owner}, ${space.id}`}
                          onSelect={() => handleSpaceSelect(space)}
                          selected={isSelected}
                          aria-checked={isSelected}
                        >
                          <span>{space.name}</span>
                        </ActionList.Item>
                      )
                    })}
                  </ActionList.Group>
                  <ActionList.Divider />
                  <ActionList.Item onSelect={handleViewAllSpaces}> View all spaces</ActionList.Item>
                  <ActionList.Divider />
                  <ActionList.Item onSelect={handleCreateSpace}>Create a space</ActionList.Item>
                </>
              ) : (
                <ActionList.Item ref={firstActionItemRef} onSelect={handleCreateSpace}>
                  Create your first space
                </ActionList.Item>
              )}
            </ActionList>
          )}
        </ActionMenu.Overlay>
      </ActionMenu>
      {showCreateSpaceDialog ? <CreateSpaceDialog closeDialog={closeCreateSpaceDialog} /> : null}
    </>
  )
}

try{ SpacesSelect.displayName ||= 'SpacesSelect' } catch {}